'use client';

// Google Analytics 4 Event Types
export type GAEventName =
  // Signup Funnel Events
  | 'signup_started' | 'signup_completed' | 'organization_created'
  // Authentication Events
  | 'login_success' | 'login_failed' | 'logout'
  // Tier Upgrade Events
  | 'tier_upgrade_initiated' | 'tier_upgrade_completed' | 'tier_upgrade_failed'
  // Feature Adoption Events
  | 'ai_use_case_created' | 'risk_assessment_created' | 'control_assigned' | 'certification_started' | 'report_generated' | 'audit_log_viewed' | 'board_report_viewed'
  // Critical Error Events
  | 'authentication_error' | 'database_error' | 'payment_error' | 'rls_policy_violation'
  // Page View Events
  | 'page_view'
  // Screen View Events
  | 'controls_management_view' | 'certifications_management_view' | 'department_analytics_view'
  | 'risk_heat_map_view' | 'audit_log_view' | 'team_members_view'
  // Button Click Events
  | 'control_assign_click' | 'certification_start_click' | 'evidence_upload_click'
  | 'invite_member_click' | 'export_report_click'
  | 'download_csv_click'| 'filter_apply_click' | 'sort_change_click' | 'pagination_click' |'view_details_click' | 'edit_click' | 'delete_click'
  // Form Submission Events
  | 'signup_form_submit' | 'login_form_submit' | 'create_organization_form_submit'
  | 'create_use_case_form_submit' | 'risk_assessment_submit' | 'invite_member_form_submit'
  | 'add_certification_form_submit' | 'assign_control_form_submit' | 'upload_evidence_form_submit'
  // Role Impersonation Events
  | 'role_impersonate_start' | 'role_impersonate_end'
  // Critical User Action Events
  | 'use_case_created' | 'risk_assessment_completed' | 'team_member_invited'
  | 'certification_added'| 'control_assigned_action' | 'evidence_uploaded' |'tier_upgraded' | 'settings_updated'
  // Empty State CTA Events
  | 'empty_use_cases_cta' | 'empty_assessments_cta' | 'empty_team_cta' |'empty_certifications_cta' | 'empty_controls_cta' | 'empty_audit_log_cta' | 'empty_risk_assessment_cta'
  // Theme Toggle
  | 'theme_toggle'
  // Engagement Metrics
  | 'time_on_dashboard' | 'scroll_depth';

export interface GAEventParams {
  // Signup funnel parameters
  signup_method?: 'email' | 'oauth';
  organization_name?: string;
  organization_type?: string;

  // Tier upgrade parameters
  from_tier?: 'tier_1' | 'tier_2' | 'tier_3';
  to_tier?: 'tier_1' | 'tier_2' | 'tier_3';
  billing_cycle?: 'monthly' | 'annual';
  upgrade_amount?: number;

  // Feature adoption parameters
  feature_name?: string;
  user_role?: 'owner' | 'admin' | 'risk_manager' | 'reviewer' | 'board_viewer';
  organization_tier?: 'tier_1' | 'tier_2' | 'tier_3';

  // Error tracking parameters
  error_type?: string;
  error_message?: string;
  error_code?: string;
  user_context?: string;

  // Screen/interaction parameters
  screen_name?: string;
  button_id?: string;
  from_theme?: 'light' | 'dark';
  to_theme?: 'light' | 'dark';
  time_seconds?: number;
  depth_percent?: number;

  // Custom dimensions
  organization_id_hash?: string;
  page_path?: string;
  page_title?: string;

  // Generic parameters
  [key: string]: any;
}

// Hash organization ID for privacy
export function hashOrganizationId(orgId: string): string {
  let hash = 0;
  for (let i = 0; i < orgId.length; i++) {
    const char = orgId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

// Initialize Google Analytics
export function initGA(): void {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV !== 'production') {
    console.log('[GA] Development mode - tracking disabled');
    return;
  }

  const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
  if (!measurementId || measurementId === 'your-google-analytics-id-here') {
    console.warn('[GA] Measurement ID not configured');
    return;
  }

  if (window.gtag) {
    console.log('[GA] Already initialized');
    return;
  }

  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true,
    cookie_flags: 'SameSite=None;Secure',
    send_page_view: false,
  });

  console.log('[GA] Initialized with ID:', measurementId);
}

// Track custom events
export function trackEvent(eventName: GAEventName, eventParams?: GAEventParams): void {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV !== 'production') {
    console.log('[GA] Event:', eventName, eventParams);
    return;
  }

  if (!window.gtag) {
    console.warn('[GA] Not initialized - event not tracked:', eventName);
    return;
  }

  try {
    window.gtag('event', eventName, eventParams || {});
  } catch (error) {
    console.error('[GA] Error tracking event:', error);
  }
}

// Track page views
export function trackPageView(url: string, title?: string): void {
  if (typeof window === 'undefined') return;
  if (process.env.NODE_ENV !== 'production') {
    console.log('[GA] Page view:', url, title);
    return;
  }

  if (!window.gtag) {
    console.warn('[GA] Not initialized - page view not tracked');
    return;
  }

  try {
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: title || document.title,
    });
  } catch (error) {
    console.error('[GA] Error tracking page view:', error);
  }
}

// Track screen views
export function trackScreenView(screenName: GAEventName, params?: GAEventParams): void {
  trackEvent(screenName, { screen_name: screenName, ...params });
}

// Track button clicks
export function trackButtonClick(eventName: GAEventName, params?: GAEventParams): void {
  trackEvent(eventName, params);
}

// Track empty state CTA clicks
export function trackEmptyStateCTA(eventName: GAEventName, screenName: string): void {
  trackEvent(eventName, { screen_name: screenName });
}

// Track theme toggle
export function trackThemeToggle(fromTheme: 'light' | 'dark', toTheme: 'light' | 'dark'): void {
  trackEvent('theme_toggle', { from_theme: fromTheme, to_theme: toTheme });
}

// Track time on dashboard
export function trackTimeOnDashboard(screenName: string, timeSeconds: number): void {
  trackEvent('time_on_dashboard', { screen_name: screenName, time_seconds: timeSeconds });
}

// Track scroll depth
export function trackScrollDepth(screenName: string, depthPercent: number): void {
  trackEvent('scroll_depth', { screen_name: screenName, depth_percent: depthPercent });
}

// Track signup funnel
export function trackSignupFunnel(step: 'started' | 'completed', params?: GAEventParams): void {
  const eventMap = {
    started: 'signup_started' as GAEventName,
    completed: 'signup_completed' as GAEventName,
  };
  trackEvent(eventMap[step], params);
}

// Track tier upgrades
export function trackTierUpgrade(
  status: 'initiated' | 'completed' | 'failed',
  fromTier: string,
  toTier: string,
  params?: GAEventParams
): void {
  const eventMap = {
    initiated: 'tier_upgrade_initiated' as GAEventName,
    completed: 'tier_upgrade_completed' as GAEventName,
    failed: 'tier_upgrade_failed' as GAEventName,
  };

  trackEvent(eventMap[status], {
    from_tier: fromTier as any,
    to_tier: toTier as any,
    ...params,
  });
}

// Track feature adoption
export function trackFeatureUsage(
  featureName: string,
  userRole?: string,
  organizationTier?: string,
  params?: GAEventParams
): void {
  const eventNameMap: Record<string, GAEventName> = {
    'ai_use_case': 'ai_use_case_created',
    'risk_assessment': 'risk_assessment_created',
    'control': 'control_assigned',
    'certification': 'certification_started',
    'report': 'report_generated',
    'audit_log': 'audit_log_viewed',
    'board_report': 'board_report_viewed',
  };

  const eventName = eventNameMap[featureName] || 'page_view';

  trackEvent(eventName, {
    feature_name: featureName,
    user_role: userRole as any,
    organization_tier: organizationTier as any,
    ...params,
  });
}

// Track critical errors
export function trackError(
  errorType: 'authentication' | 'database' | 'payment' | 'rls_policy',
  errorMessage: string,
  errorCode?: string,
  userContext?: string
): void {
  const eventMap = {
    authentication: 'authentication_error' as GAEventName,
    database: 'database_error' as GAEventName,
    payment: 'payment_error' as GAEventName,
    rls_policy: 'rls_policy_violation' as GAEventName,
  };

  trackEvent(eventMap[errorType], {
    error_type: errorType,
    error_message: errorMessage,
    error_code: errorCode,
    user_context: userContext,
  });
}

// Track form submissions
export function trackFormSubmit(
  formName: string,
  status: 'success' | 'failure',
  params?: GAEventParams
): void {
  const eventMap: Record<string, GAEventName> = {
    signup_form: 'signup_form_submit',
    login_form: 'login_form_submit',
    create_organization_form: 'create_organization_form_submit',
    create_use_case_form: 'create_use_case_form_submit',
    risk_assessment: 'risk_assessment_submit',
    invite_member_form: 'invite_member_form_submit',
    add_certification_form: 'add_certification_form_submit',
    assign_control_form: 'assign_control_form_submit',
    upload_evidence_form: 'upload_evidence_form_submit',
  };
  const eventName = eventMap[formName] || 'page_view';
  trackEvent(eventName, { status, ...params });
}

// Track role impersonation
export function trackRoleImpersonation(
  action: 'start' | 'end',
  fromRole: string,
  toRole: string,
  params?: GAEventParams
): void {
  const eventName: GAEventName = action === 'start' ? 'role_impersonate_start' : 'role_impersonate_end';
  trackEvent(eventName, { from_role: fromRole, to_role: toRole, ...params });
}

// Track critical user actions
export function trackCriticalAction(
  action: 'use_case_created' | 'risk_assessment_completed' | 'team_member_invited' | 'certification_added' | 'control_assigned_action' | 'evidence_uploaded' | 'tier_upgraded' | 'settings_updated',
  organizationIdHash?: string,
  userRole?: string,
  params?: GAEventParams
): void {
  trackEvent(action, {
    organization_id_hash: organizationIdHash,
    user_role: userRole as any,
    ...params,
  });
}

// ── Onboarding Tracking ──
export function trackOnboardingStep(
  step: 1 | 2 | 3 | 4 | 5,
  params?: GAEventParams
): void {
  const eventMap: Record<number, GAEventName> = {
    1: 'onboarding_step_1_completed',
    2: 'onboarding_step_2_completed',
    3: 'onboarding_step_3_completed',
    4: 'onboarding_step_4_completed',
    5: 'onboarding_step_5_completed',
  };
  trackEvent(eventMap[step], params);
}

export function trackOnboardingCompleted(totalDurationSeconds: number, orgTier?: string): void {
  // High-value conversion event — tagged as conversion
  trackEvent('onboarding_completed', {
    total_duration: totalDurationSeconds,
    org_tier: orgTier,
    conversion: true,
  });
}

export function trackOnboardingSkipped(stepSkipped: number, reason?: string): void {
  trackEvent('onboarding_skipped', { step_skipped: stepSkipped, reason });
}

// ── Export Tracking ──
export function trackExportInitiated(
  exportType: string,
  format: string,
  dateRangeDays: number,
  orgTier?: string
): void {
  trackEvent('export_initiated', { export_type: exportType, format, date_range_days: dateRangeDays, org_tier: orgTier });
}

export function trackExportCompleted(
  exportType: string,
  format: string,
  fileSizeKb: number,
  generationTimeMs: number
): void {
  trackEvent('export_completed', { export_type: exportType, format, file_size_kb: fileSizeKb, generation_time_ms: generationTimeMs });
  // High-value conversion signal for full audit package
  if (exportType === 'full_audit_package') {
    trackEvent('full_audit_package_exported', { conversion: true });
  }
}

export function trackExportFailed(exportType: string, errorReason: string): void {
  trackEvent('export_failed', { export_type: exportType, error_reason: errorReason });
}

export function trackExportScheduled(exportType: string, frequency: string): void {
  trackEvent('export_scheduled', { export_type: exportType, frequency });
}

// ── Alert Configuration Tracking ──
export function trackAlertRuleCreated(eventType: string, recipientCount: number, threshold?: number): void {
  trackEvent('alert_rule_created', { event_type: eventType, recipient_count: recipientCount, threshold });
}

export function trackAlertRuleUpdated(eventType: string, fieldChanged: string): void {
  trackEvent('alert_rule_updated', { event_type: eventType, field_changed: fieldChanged });
}

export function trackAlertRuleDeleted(eventType: string): void {
  trackEvent('alert_rule_deleted', { event_type: eventType });
}

export function trackAlertRuleTested(eventType: string, deliverySuccess: boolean): void {
  trackEvent('alert_rule_tested', { event_type: eventType, delivery_success: deliverySuccess });
}

export function trackAlertPreferencesUpdated(alertsEnabledCount: number): void {
  trackEvent('alert_preferences_updated', { alerts_enabled: alertsEnabledCount });
}

export function trackEscalationTriggerMapped(escalationType: string, alertRuleType: string): void {
  trackEvent('escalation_trigger_mapped', { escalation_type: escalationType, alert_rule_type: alertRuleType });
}

// Type declarations for window.gtag
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}
